
import { ShieldCheck, ShieldAlert, AlertTriangle, ShieldX } from 'lucide-react';

export type RiskLevel = 'Super Safe' | 'Low Risk' | 'Moderate Risk' | 'High Risk' | 'Super High Risk';

interface RiskConfig {
  icon: typeof ShieldCheck;
  color: string;
  textColor: string;
  borderColor: string;
  iconColor: string;
  description: string;
}

export const getRiskConfig = (companyName: string, riskLevel: RiskLevel): RiskConfig => {
  // Configuration for different risk levels
  const riskConfigs: Record<RiskLevel, Omit<RiskConfig, 'description'>> = {
    'Super Safe': {
      icon: ShieldCheck,
      color: 'bg-[#F2FCE2]',
      textColor: 'text-green-700',
      borderColor: 'border-green-200',
      iconColor: 'text-green-500',
    },
    'Low Risk': {
      icon: ShieldCheck,
      color: 'bg-[#E5DEFF]',
      textColor: 'text-indigo-700',
      borderColor: 'border-indigo-200',
      iconColor: 'text-indigo-500',
    },
    'Moderate Risk': {
      icon: AlertTriangle,
      color: 'bg-[#FEF7CD]',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
      iconColor: 'text-amber-500',
    },
    'High Risk': {
      icon: ShieldAlert,
      color: 'bg-[#FEC6A1]',
      textColor: 'text-orange-700',
      borderColor: 'border-orange-200',
      iconColor: 'text-orange-500',
    },
    'Super High Risk': {
      icon: ShieldX,
      color: 'bg-red-100',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      iconColor: 'text-red-500',
    }
  };
  
  // Risk level specific descriptions
  const descriptions: Record<RiskLevel, string> = {
    'Super Safe': `Based on our analysis, ${companyName} appears to be a stable employer with minimal redundancy risk.`,
    'Low Risk': `${companyName} shows indicators of stability with low probability of layoffs.`,
    'Moderate Risk': `${companyName} has some factors that suggest a moderate level of redundancy risk.`,
    'High Risk': `Our analysis indicates ${companyName} has concerning patterns that suggest a higher risk of redundancies.`,
    'Super High Risk': `Based on multiple indicators, ${companyName} shows significant warning signs of potential workforce reductions.`,
  };

  return {
    ...riskConfigs[riskLevel],
    description: descriptions[riskLevel]
  };
};

